/**
 * Legal A/B — the app side.
 *
 * The funnel buckets the person into `legal_<base onboarding>` (variants
 * `control` / `legal`, by the `legal_cohort` person property the funnel set)
 * and tracks the exposure. The app reads the same flag after identify and
 * applies its payload to the in-app changes that are not part of any
 * onboarding config:
 *
 *   { "restoreConfirm": "legal", "winbackAfterCancel": "legal", "upsellLegalAbove": "legal" }
 *
 * A key is "legal" or "control"; a missing key, a missing payload or an
 * unreadable flag means the control (previous) behaviour. Without a flag the
 * purchase onboarding decides: a twin (`<base>_std`) gets the legal set.
 */
export const LEGAL_FLAG_PREFIX = "legal_";
export const LEGAL_ONBOARDING_SUFFIX = "_std";

export type LegalFeature = "restoreConfirm" | "winbackAfterCancel" | "upsellLegalAbove";
export const LEGAL_FEATURES: readonly LegalFeature[] = ["restoreConfirm", "winbackAfterCancel", "upsellLegalAbove"];

export type LegalConfig = Record<LegalFeature, boolean>;

export const CONTROL_LEGAL_CONFIG: LegalConfig = { restoreConfirm: false, winbackAfterCancel: false, upsellLegalAbove: false };
export const FULL_LEGAL_CONFIG: LegalConfig = { restoreConfirm: true, winbackAfterCancel: true, upsellLegalAbove: true };

export type LegalFlag = { key: string; variant: string; payload: unknown };

/**
 * The legal flag the person is bucketed into, out of an exposure-free flags
 * snapshot (FlagsClient.all()): a `legal_*` flag has a value only when the
 * person's legal_cohort matched one of its conditions. Null = not in any
 * legal experiment.
 */
export const findLegalFlagKey = (flags: Record<string, unknown>): string | null => {
    const keys = Object.keys(flags)
        .filter((key) => key.startsWith(LEGAL_FLAG_PREFIX))
        .sort();
    return keys[0] ?? null;
};

const isLegal = (value: unknown): boolean => typeof value === "string" && value.trim().toLowerCase() === "legal";

/**
 * The feature set of a resolved flag. Each payload key decides its feature;
 * a `legal` variant without a payload enables everything, `control` or an
 * unknown variant nothing.
 */
export const legalConfigOfFlag = (flag: Pick<LegalFlag, "variant" | "payload">): LegalConfig => {
    const payload = flag.payload && typeof flag.payload === "object" ? (flag.payload as Record<string, unknown>) : null;
    if (!payload) return isLegal(flag.variant) ? { ...FULL_LEGAL_CONFIG } : { ...CONTROL_LEGAL_CONFIG };

    const config = { ...CONTROL_LEGAL_CONFIG };
    for (const feature of LEGAL_FEATURES) {
        config[feature] = isLegal(payload[feature]);
    }
    return config;
};

/** Fallback without a flag: the twin onboarding carries the legal set. */
export const legalConfigOfOnboarding = (onboardingId: string | null | undefined): LegalConfig =>
    onboardingId && onboardingId.endsWith(LEGAL_ONBOARDING_SUFFIX)
        ? { ...FULL_LEGAL_CONFIG }
        : { ...CONTROL_LEGAL_CONFIG };
